import React from "react";
import { classNames } from "./Utils";
import * as MD from "reports/measures/MeasureDef";
import * as MP from "reports/measures/MeasureProperties";
import * as SR from "reports/measures/SeverityResolver";

const cleanLabel = (label) => {
    let limit = 20;
    if (label.length <= limit) {
        return <div className="text-gray-700 text-sm font-medium leading-5">{label}</div>;
    }
    return (
        <div className="text-gray-700 text-sm font-medium relative has-tooltip leading-5 flex">
            <span className="tooltip rounded shadow-lg p-1 ml-10 mt-4">{label}</span>
            <div className="hs-tooltip">{label.substring(0, limit) + "..."}</div>
        </div>
    );
};

export const MotionRomTypeMap = Object.freeze({
    "LeftShoulderAbduction": MD.MeasureTypesMap.LeftAbduction,
    "LeftShoulderFlexion": MD.MeasureTypesMap.LeftFlexion,
    "LeftShoulderScaption": MD.MeasureTypesMap.LeftScaption,
    "RightShoulderAbduction": MD.MeasureTypesMap.RightAbduction,
    "RightShoulderFlexion": MD.MeasureTypesMap.RightFlexion,
    "RightShoulderScaption": MD.MeasureTypesMap.RightScaption,
    "BackFlexion": MD.MeasureTypesMap.LowerFlexion,
    "NeckExtension": MD.MeasureTypesMap.NeckExtension,
    "NeckFlexion": MD.MeasureTypesMap.NeckFlexion,
    "NeckLeftRotation": MD.MeasureTypesMap.NeckLeftRotation,
    "NeckRightRotation": MD.MeasureTypesMap.NeckRightRotation
});

export const isUpperExtremityRom = (romType) => MP.MeasurePropertiesMap[romType].categories.includes(MD.MeasureCategory.UPPER_ROM);
export const isBackRom = (romType) => MP.MeasurePropertiesMap[romType].categories.includes(MD.MeasureCategory.LOWER_ROM);
export const isNeckRom = (romType) => MP.MeasurePropertiesMap[romType].categories.includes(MD.MeasureCategory.NECK_ROM);
export const romMaxValue = (romType) => MP.MeasurePropertiesMap[romType].maxScore;
export const romNormalizedValue = (romMeasureType, romValue) => Math.round((Math.min(Math.round(romValue), MP.MeasurePropertiesMap[romMeasureType].maxScore) / MP.MeasurePropertiesMap[romMeasureType].maxScore) * 100);

const getSeverity = (romMeasureType, romValue) => MP.MeasurePropertiesMap[romMeasureType].severityResolver(romValue).severity;

export const romBarColor = (romMeasureType, romValue) => {
    switch (getSeverity(romMeasureType, romValue)) {
        case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "bg-error-300";
        case SR.Severity.SEVERE: return "bg-warning-300";
        case SR.Severity.MODERATE: return "bg-yellow-300";
        case SR.Severity.MILD: return "bg-green-light-300";
        case SR.Severity.LOW: return "bg-success-300";
        default: return "bg-gray-300";
    }
};

export const romBadgeTextColor = (romMeasureType, romValue) => {
    switch (getSeverity(romMeasureType, romValue)) {
        case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "text-error-700";
        case SR.Severity.SEVERE: return "text-warning-700";
        case SR.Severity.MODERATE: return "text-yellow-700";
        case SR.Severity.MILD: return "text-green-light-700";
        case SR.Severity.LOW: return "text-success-700";
        default: return "text-gray-700";
    }
};

export const romBadgeColor = (romMeasureType, romValue) => {
    switch (getSeverity(romMeasureType, romValue)) {
        case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "bg-error-50";
        case SR.Severity.SEVERE: return "bg-warning-50";
        case SR.Severity.MODERATE: return "bg-yellow-50";
        case SR.Severity.MILD: return "bg-green-light-50";
        case SR.Severity.LOW: return "bg-success-50";
        default: return "bg-gray-50";
    }
};

export const romLevel = (romMeasureType, romValue) => {
    switch (getSeverity(romMeasureType, romValue)) {
        case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "Sev. Limited";
        case SR.Severity.SEVERE: return "Mod. Limited";
        case SR.Severity.MODERATE: return "Min. Limited";
        case SR.Severity.MILD: return "Functional";
        case SR.Severity.LOW: return "Full Function";
        default: return "Unknown";
    }
};

export const romTooltip = (romMeasureType, romValue) => {
    const severity = getSeverity(romMeasureType, romValue);
    if (isBackRom(romMeasureType)) {
        switch (severity) {
            case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "Severely Limited 0°-15°";
            case SR.Severity.SEVERE: return "Moderately Limited 16°-60°";
            case SR.Severity.MODERATE: return "Minimally Limited 61°-75°";
            case SR.Severity.MILD: return "Functional 76°-85°";
            case SR.Severity.LOW: return "Full Function 86°-90°";
        }
    }
    else if (isNeckRom(romMeasureType)) {
        if (romMeasureType === MD.MeasureTypesMap.NeckExtension) {
            switch (severity) {
                case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "Severely Limited 0°-24°";
                case SR.Severity.SEVERE: return "Moderately Limited 25°-36°";
                case SR.Severity.MODERATE: return "Minimally Limited 37°-48°";
                case SR.Severity.MILD: return "Functional 49°-57°";
                case SR.Severity.LOW: return "Full Function 58-60°";
            }
        }
        else if (romMeasureType === MD.MeasureTypesMap.NeckFlexion) {
            switch (severity) {
                case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "Severely Limited 0°-20°";
                case SR.Severity.SEVERE: return "Moderately Limited 21°-30°";
                case SR.Severity.MODERATE: return "Minimally Limited 31°-40°";
                case SR.Severity.MILD: return "Functional 41°-47°";
                case SR.Severity.LOW: return "Full Function 48-50°";
            }
        }
        else { // Rotation
            switch (severity) {
                case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "Severely Limited 0°-32°";
                case SR.Severity.SEVERE: return "Moderately Limited 33°-48°";
                case SR.Severity.MODERATE: return "Minimally Limited 49°-64°";
                case SR.Severity.MILD: return "Functional 65°-76°";
                case SR.Severity.LOW: return "Full Function 77-80°";
            }
        }
    }
    else { // Upper Extremity
        switch (severity) {
            case SR.Severity.EXTREME: case SR.Severity.VERY_SEVERE: return "Severely Limited 0°-39°";
            case SR.Severity.SEVERE: return "Moderately Limited 30°-119°";
            case SR.Severity.MODERATE: return "Minimally Limited 120°-144°";
            case SR.Severity.MILD: return "Functional 145°-164°";
            case SR.Severity.LOW: return "Full Function 165°-180°";
        }
    }
    return "";
};

export const renderRomRow = (key, romLabel, romMeasureType, romValue) => {
    return (
        <React.Fragment key={key}>
            <div className="flex items-center gap-2 border-red-600 border-2">
                <img className="w-5 h-5" src="/icons/compass.svg" alt="icon" />
                {cleanLabel(romLabel)}
            </div>
            <div className="flex items-center gap-4 w-full">
                <div className="grow shrink relative h-5 flex items-center">
                    <div className="w-full absolute h-2 bg-gray-200 rounded-full" />
                    <div className={classNames("h-2 absolute rounded-full", romBarColor(romMeasureType, romValue))} style={{ width: `${romNormalizedValue(romMeasureType, romValue)}%` }} />
                </div>
                <span className="inline-flex gap-2 text-sm items-center">
                    {`${Math.round(romValue)}°`}
                    <div className={`px-2 py-0.5 has-tooltip relative rounded-2xl justify-center items-center flex ${romBadgeColor(romMeasureType, romValue)}`}>
                        <span className="tooltip rounded shadow-lg p-1 -mt-12">{romTooltip(romMeasureType, romValue)}</span>
                        <div className={`text-center hs-tooltip text-xs font-medium leading-[18px] text-nowrap ${romBadgeTextColor(romMeasureType, romValue)}`}>{romLevel(romMeasureType, romValue)}</div>
                    </div>
                </span>
            </div>
        </React.Fragment>
    );
};